import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-985e1c90"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["value", "placeholder", "disabled"]

import { addSeparators } from '@/mixins/format-numbers.mixin';
import BigNumber from 'bignumber.js';
import { computed, nextTick, onMounted, ref, watch } from 'vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'FormattedInput',
  props: {
    modelValue: null,
    placeholder: null,
    disabled: { type: Boolean },
    focus: { type: Boolean },
    decimals: null
  },
  emits: ["update:modelValue", "update:focus", "input", "blur", "focus", "change"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:modelValue', value: string): void;
  (e: 'update:focus', value: boolean): void;
  (e: 'input', value: string): void;
  (e: 'blur', value: FocusEvent): void;
  (e: 'focus', value: FocusEvent): void;
  (e: 'change', value: string): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  focus?: boolean;
  decimals?: number;
}





const separator = ' ';
const defaultPlaceholder = '0.0';

const inputRef = ref<HTMLInputElement | null>(null);

const inputValueRef = ref(props.modelValue || '');

const modelValue = computed(() => props.modelValue || '');

const formattedModel = computed(() => {
  const val = shortenByDecimals(modelValue.value);

  const isEndsWithDot = val.slice(-1) === '.';
  const formattedValue = addSeparators(removeSeparators(val));

  return isEndsWithDot ? formattedValue + '.' : formattedValue;
});

const placeholder = computed(() =>
  props.placeholder != undefined ? props.placeholder : defaultPlaceholder,
);

watch(formattedModel, val => (inputValueRef.value = val));

onMounted(() => {
  const inputValue = inputRef.value?.value;
  const inputValueFormatted = inputValue
    ? new BigNumber(removeSeparators(inputValue)).toFixed()
    : '0';

  if (!inputValue || !new BigNumber(inputValueFormatted).gt(0)) {
    inputValueRef.value = '';
  }

  watch(
    () => props.focus,
    val => {
      if (!inputRef.value || val == undefined) {
        return;
      }

      if (document.activeElement === inputRef.value && !val) {
        inputRef.value.blur();
        return;
      }

      if (document.activeElement !== inputRef.value && val) {
        inputRef.value.focus();
        return;
      }
    },
    {
      immediate: true,
    },
  );
});

const onInput = (event: InputEvent) => {
  const { inputValue, isDeletingEvent, isSpaceEntered, caretPositionCurrent } =
    unpackInputEvent(event);

  let caretPositionToSet = caretPositionCurrent;
  let inputValueFiltered = removeSeparators(replaceCommas(inputValue));

  if (!isDeletingEvent) {
    [inputValueFiltered, caretPositionToSet] = checkAndAddZeroAndDot(
      inputValueFiltered,
      caretPositionToSet,
    );
  }

  if ((!isDeletingEvent && !validate(inputValueFiltered)) || isSpaceEntered) {
    inputRef.value!.value = inputValueRef.value;
    return;
  }

  inputValueFiltered = shortenByDecimals(inputValueFiltered);
  inputRef.value!.value = addSeparators(shortenByDecimals(inputValueFiltered));

  let valueToSet = addSeparators(inputValueFiltered) + getTailDotOrEmpty(inputValueFiltered);

  caretPositionToSet += getCaretPositionShift(
    inputValue,
    valueToSet,
    caretPositionCurrent,
    caretPositionToSet,
  );

  if (isDeletingEvent && isOnlySeparatorDeleted(valueToSet)) {
    [valueToSet, caretPositionToSet] = removeSeparatorAndPreviousSymbol(
      valueToSet,
      caretPositionToSet,
    );
  }

  setInputValue(valueToSet, caretPositionToSet);

  emitIfModelChanges(valueToSet, isDeletingEvent);
};

const onBlur = (event: FocusEvent): void => {
  const inputValue = inputRef.value!.value;
  emit('blur', event);
  emit('update:focus', false);
  const inputValueFormatted = new BigNumber(removeSeparators(inputValue)).toFixed();

  if (!inputValue || !new BigNumber(inputValueFormatted).gt(0)) {
    inputValueRef.value = '';
    emit('update:modelValue', '');
    emit('change', '');
    emit('input', '');
    return;
  }

  setInputValue(addSeparators(inputValueFormatted));
  emit('update:modelValue', inputValueFormatted);
  emit('change', inputValueFormatted);
  emit('input', inputValueFormatted);
};

const onFocus = (event: FocusEvent): void => {
  emit('focus', event);
  emit('update:focus', true);
};

function setInputValue(value: string, caretPosition?: number): void {
  const input = inputRef.value;
  if (!input) {
    return;
  }
  if (caretPosition === undefined) {
    caretPosition = input.selectionStart!;
  }
  inputValueRef.value = value;

  nextTick(() => input.setSelectionRange(caretPosition!, caretPosition!));
}

function removeSeparators(value: string): string {
  return value.replaceAll(separator, '');
}

function replaceCommas(value: string): string {
  return value.replaceAll(',', '.');
}

function checkAndAddHeadingZero(value: string): string {
  return value[0] === '.' ? `0${value}` : value;
}

function checkAndAddDot(value: string): string {
  return value[0] === '0' && value[1] !== '.' ? `0.${value.slice(1)}` : value;
}

function validate(value: string): boolean {
  return new RegExp(`^([0-9]+[.])?[0-9]*$`).test(removeSeparators(value));
}

function unpackInputEvent(event: InputEvent): {
  inputValue: string;
  isDeletingEvent: boolean;
  isSpaceEntered: boolean;
  caretPositionCurrent: number;
} {
  const inputElement = event.target as HTMLInputElement;
  return {
    inputValue: inputElement.value,
    isDeletingEvent: event.inputType.includes('delete'),
    isSpaceEntered: event.data === ' ',
    caretPositionCurrent: inputElement.selectionStart!,
  };
}

function shortenByDecimals(value: string): string {
  const integerAndDecimalParts = value.split('.');

  if (props.decimals && integerAndDecimalParts.length > 1) {
    const integerPart = integerAndDecimalParts[0];
    const decimalPart = integerAndDecimalParts[1].substr(0, props.decimals);
    return integerPart + '.' + decimalPart;
  }
  return value;
}

function checkAndAddZeroAndDot(value: string, caretPosition: number): [string, number] {
  const filteredInputValueLength = value.length;
  value = checkAndAddHeadingZero(value);

  if (value !== '0') {
    value = checkAndAddDot(value);
  }

  caretPosition += value.length - filteredInputValueLength;
  return [value, caretPosition];
}

function getTailDotOrEmpty(value: string): '.' | '' {
  return value[value.length - 1] === '.' ? '.' : '';
}

function getCaretPositionShift(
  inputValue: string,
  valueToSet: string,
  caretPositionCurrent: number,
  caretPositionToSet,
) {
  const spacesBeforeCaretInCurrentValue =
    inputValue.slice(0, caretPositionCurrent).split(separator).length - 1;

  const spacesBeforeCaretInNewValue =
    valueToSet.slice(0, caretPositionToSet).split(separator).length - 1;

  return spacesBeforeCaretInNewValue - spacesBeforeCaretInCurrentValue;
}

function isOnlySeparatorDeleted(value: string): boolean {
  return removeSeparators(value) === removeSeparators(inputValueRef.value);
}

function removeSeparatorAndPreviousSymbol(
  valueToSet: string,
  caretPositionCurrent: number,
): [string, number] {
  valueToSet = addSeparators(
    removeSeparators(
      valueToSet.slice(0, caretPositionCurrent - 1) + valueToSet.slice(caretPositionCurrent),
    ),
  );

  const caretPositionToSet = caretPositionCurrent <= 1 ? 0 : caretPositionCurrent - 1;
  return [valueToSet, caretPositionToSet];
}

function emitIfModelChanges(valueToSet: string, isDeletingEvent: boolean): void {
  const tailDot = getTailDotOrEmpty(valueToSet);
  const previousValue = new BigNumber(removeSeparators(modelValue.value));
  const currentValue = new BigNumber(removeSeparators(valueToSet));
  if (!previousValue.eq(currentValue) && (isDeletingEvent || !tailDot)) {
    emit('update:modelValue', removeSeparators(valueToSet));
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("input", {
    ref_key: "inputRef",
    ref: inputRef,
    value: inputValueRef.value,
    onInput: onInput,
    onBlur: onBlur,
    onFocus: onFocus,
    placeholder: _unref(placeholder),
    disabled: __props.disabled
  }, null, 40, _hoisted_1))
}
}

})